import { motion } from 'framer-motion';
import { Bot, Plug, Calendar, MessageSquare, Users } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const aiFeatures = [
  {
    icon: <Plug className="w-6 h-6" />,
    title: "Seamless Integration",
    description: "Connect with your existing tools and software effortlessly"
  },
  {
    icon: <Calendar className="w-6 h-6" />,
    title: "Smart Scheduling",
    description: "Automated appointment booking and calendar management"
  },
  {
    icon: <MessageSquare className="w-6 h-6" />,
    title: "Virtual Assistant",
    description: "24/7 customer support and task automation"
  },
  {
    icon: <Users className="w-6 h-6" />,
    title: "Live Handoff",
    description: "Smooth transition from AI to human agents when needed"
  }
];

const ChatMessage = ({ isBot, children, delay = 0 }) => {
  const { theme } = useTheme();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className={`flex ${isBot ? '' : 'justify-end'} gap-3`}
    >
      {isBot && (
        <div className={`w-6 h-6 rounded-full ${theme === 'dark' ? 'bg-blue-500/20' : 'bg-blue-100'} flex items-center justify-center flex-shrink-0`}>
          <Bot className="w-4 h-4 text-blue-400" />
        </div>
      )}
      <div className={`${
        isBot 
          ? theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'
          : theme === 'dark' ? 'bg-blue-500/20' : 'bg-blue-50'
      } rounded-lg p-3 text-sm ${
        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
      } max-w-[80%]`}>
        {children}
      </div>
    </motion.div>
  );
};

const TypingIndicator = ({ delay = 0 }) => {
  const { theme } = useTheme();
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: [0, 1, 0] }}
      transition={{ delay, duration: 1, repeat: 1 }}
      className="flex gap-3"
    >
      <div className={`w-6 h-6 rounded-full ${theme === 'dark' ? 'bg-blue-500/20' : 'bg-blue-100'} flex items-center justify-center flex-shrink-0`}>
        <Bot className="w-4 h-4 text-blue-400" />
      </div>
      <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'} rounded-lg p-3 flex gap-1`}>
        <motion.div
          className={`w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-gray-500' : 'bg-gray-400'}`}
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 0.6, repeat: Infinity }}
        />
        <motion.div
          className={`w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-gray-500' : 'bg-gray-400'}`}
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 0.6, delay: 0.2, repeat: Infinity }}
        />
        <motion.div
          className={`w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-gray-500' : 'bg-gray-400'}`}
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 0.6, delay: 0.4, repeat: Infinity }}
        />
      </div>
    </motion.div>
  );
};

const ChatInterface = () => {
  const { theme } = useTheme();
  
  return (
    <div className={`${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} rounded-xl overflow-hidden shadow-xl w-full max-w-md`}>
      <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'} p-4 flex items-center gap-3`}>
        <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
          <Bot className="w-5 h-5 text-white" />
        </div>
        <div>
          <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>AI Assistant</h4>
          <p className="text-xs text-green-400">Online</p>
        </div>
      </div>

      <div className="p-4 space-y-4 h-[400px] overflow-y-auto">
        <ChatMessage isBot delay={0.2}>
          Hi! I'm your AI assistant. How can I help you today?
        </ChatMessage>

        <ChatMessage isBot={false} delay={1}>
          I'd like to schedule a consultation for next week
        </ChatMessage>

        <TypingIndicator delay={1.8} />

        <ChatMessage isBot delay={3}>
          I'd be happy to help you schedule a consultation. What's your preferred day next week?
        </ChatMessage>

        <ChatMessage isBot={false} delay={4}>
          Tuesday afternoon would be great
        </ChatMessage>

        <TypingIndicator delay={4.8} />

        <ChatMessage isBot delay={6}>
          I'm checking the available slots for next Tuesday...
          {'\n\n'}
          I found several openings:
          {'\n'}• 2:00 PM
          {'\n'}• 3:30 PM
          {'\n'}• 4:45 PM
          {'\n\n'}
          Which time works best for you?
        </ChatMessage>

        <ChatMessage isBot={false} delay={7}>
          3:30 PM works perfect
        </ChatMessage>

        <TypingIndicator delay={7.8} />

        <ChatMessage isBot delay={9}>
          Great! I'll schedule your consultation for next Tuesday at 3:30 PM.
          {'\n\n'}
          Could you please provide your name and email to confirm the booking?
        </ChatMessage>

        <ChatMessage isBot={false} delay={10}>
          John Smith, john@example.com
        </ChatMessage>

        <TypingIndicator delay={10.8} />

        <ChatMessage isBot delay={12}>
          Perfect! I've scheduled your consultation for Tuesday at 3:30 PM.
          {'\n\n'}
          You'll receive a confirmation email shortly with the meeting details and a calendar invite.
          {'\n\n'}
          Is there anything else you'd like to know about our services?
        </ChatMessage>
      </div>

      <div className={`p-4 border-t ${theme === 'dark' ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className={`flex gap-2 items-center ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'} rounded-lg px-3 py-2`}>
          <input
            type="text"
            placeholder="Type your message..."
            className={`bg-transparent ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} text-sm flex-1 focus:outline-none`}
            disabled
          />
          <button className="text-blue-400 hover:text-blue-300">
            <MessageSquare className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default function AIMLSection() {
  const { theme } = useTheme();
  
  return (
    <section id="ai-ml-section" className={`py-20 px-4 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Bot className="w-12 h-12 text-blue-500" />
          <h2 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>AI Agents</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="prose prose-invert max-w-none">
              <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                Our AI agents provide intelligent automation and seamless customer interactions, handling everything from appointment scheduling to complex inquiries with natural conversation flow.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {aiFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow-lg'} p-6 rounded-xl`}
                >
                  <div className="text-blue-500 mb-4">{feature.icon}</div>
                  <h3 className={`text-xl font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {feature.title}
                  </h3>
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="flex justify-center">
            <ChatInterface />
          </div>
        </div>
      </div>
    </section>
  );
}